import React, { type HTMLAttributes, type KeyboardEventHandler, type ReactEventHandler, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import useEventCallback from '@jwp/ott-hooks-react/src/useEventCallback';
import classNames from 'classnames';

import Grow from '../Animation/Grow/Grow';
import { useModal } from '../../hooks/useModal';

import styles from './Modal.module.scss';

export type AnimationProps = {
  open?: boolean;
  duration?: number;
  delay?: number;
  children: React.ReactNode;
  className?: string;
  onCloseAnimationEnd?: () => void;
};

type Props = {
  children?: React.ReactNode;
  className?: string;
  centered?: boolean;
  AnimationComponent?: React.ElementType<AnimationProps>;
  open: boolean;
  useNativeDialog?: boolean;
  disabledBtn?: boolean;
  onClose?: () => void;
  animationContainerClassName?: string;
  role?: HTMLAttributes<HTMLElement>['role'];
  elevation?: 'default' | 'high';
} & React.AriaAttributes;

const Modal: React.FC<Props> = ({
  open,
  onClose,
  children,
  className,
  centered,
  role,
  useNativeDialog = true,
  disabledBtn,
  AnimationComponent = Grow,
  animationContainerClassName,
  elevation = 'default',
  ...ariaAttributes
}: Props) => {
  const modalRef = useRef<HTMLDialogElement>() as React.MutableRefObject<HTMLDialogElement>;

  const { handleOpen, handleClose } = useModal();

  const closeModalEvent = useEventCallback(() => {
    const onAnimationEndHandler = () => {
      // modalRef.current?.close();
      modalRef.current?.classList.remove(styles.close);
      modalRef.current?.removeEventListener('animationend', onAnimationEndHandler);
    };

    if (modalRef.current?.open) {
      modalRef.current.addEventListener('animationend', onAnimationEndHandler);
      modalRef.current.classList.add(styles.close);
    }
  });

  const openModalEvent = useEventCallback(() => {
    handleOpen();
    modalRef.current?.show();
    modalRef.current?.classList.remove(styles.close);
  });

  const keyDownHandler: KeyboardEventHandler<HTMLDialogElement> = (event) => {
    if (event.key === 'Escape' && modalRef.current.contains(event.target as HTMLElement)) {
      event.preventDefault();
      onClose?.();
    }
  };

  const closeHandler: ReactEventHandler<HTMLDialogElement> = (event) => {
    if (modalRef.current.contains(event.target as HTMLElement)) {
      onClose?.();
      handleClose();
    }
  };

  const closeAnimationEndHandler = () => {
    modalRef.current?.close();
  };

  useEffect(() => {
    if (!useNativeDialog) {
      if (open) {
        handleOpen();
      } else {
        handleClose();
      }
      return;
    }

    if (open) {
      openModalEvent();
    } else {
      closeModalEvent();
    }
  }, [openModalEvent, closeModalEvent, handleOpen, handleClose, open, useNativeDialog]);

  const clickHandler: ReactEventHandler<HTMLDialogElement> = (event) => {
    // Backdrop click (the dialog itself) will close the modal
    if (event.target === modalRef.current) {
      if (disabledBtn) {
        onClose?.();
        handleClose();
      }
    }
  };

  if (!open && !useNativeDialog) {
    return null;
  }

  if (!useNativeDialog) {
    return ReactDOM.createPortal(
      <div
        className={classNames(styles.legacyModal, className, { [styles.centeredLegacy]: centered, [styles.elevationHigh]: elevation === 'high' })}
        role={role}
        {...ariaAttributes}
      >
        <div className={styles.legacyBackdrop} onClick={disabledBtn ? onClose : undefined} />
        <AnimationComponent open={open} duration={300} className={animationContainerClassName}>
          {children}
        </AnimationComponent>
      </div>,
      document.querySelector('body') as HTMLElement,
    );
  }

  return ReactDOM.createPortal(
    <dialog
      className={classNames(className, { [styles.centered]: centered, [styles.elevationHigh]: elevation === 'high' })}
      onKeyDown={keyDownHandler}
      onClose={closeHandler}
      onClick={clickHandler}
      ref={modalRef}
      role={role}
      {...ariaAttributes}
    >
      <AnimationComponent open={open} duration={300} className={animationContainerClassName} onCloseAnimationEnd={closeAnimationEndHandler}>
        {children}
      </AnimationComponent>
    </dialog>,
    document.querySelector('body') as HTMLElement,
  );
};

export default Modal;
